import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AGENT_PLAN,
  APP_HOME_URL,
  BROKER_PLAN,
  ENTERPRISE_PLAN,
  MULTI_OFFICE_PLAN,
  SEAT_TIERS,
} from "../config";
import MembershipEnrollmentModal from "../components/MembershipEnrollmentModal";
import "../styles/pricing.css";

const TRUST_BADGES = [
  "Secure Payments",
  "Professional Platform",
  "Data Belongs to You",
  "Cancel Anytime",
  "No Hidden Fees",
];

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
}

function displayPrice(monthly, annual, cycle) {
  if (monthly == null) return { main: "Custom Pricing", sub: "" };
  if (cycle === "annual") return { main: formatMoney(annual), sub: "per year" };
  return { main: formatMoney(monthly), sub: "per month" };
}

export default function Pricing() {
  const [cycle, setCycle] = useState("monthly");
  const [brokerSeat, setBrokerSeat] = useState("10");
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollPlan, setEnrollPlan] = useState("agent");

  const seat = useMemo(
    () => SEAT_TIERS.find((row) => row.id === brokerSeat) || SEAT_TIERS[0],
    [brokerSeat]
  );

  const agentPrice = displayPrice(AGENT_PLAN.monthly, AGENT_PLAN.annual, cycle);
  const brokerPrice = displayPrice(seat.monthly, seat.annual, cycle);

  function openEnroll(planId = "agent") {
    setEnrollPlan(planId);
    setEnrollOpen(true);
  }

  return (
    <article className="pricing-page">
      <header className="pricing-banner">
        <p className="pricing-banner__kicker">DreamHome Journey · Membership</p>
        <h1>Elevate Your Client Experience.</h1>
        <p className="pricing-banner__sub">
          This is a professional membership system for real estate workflows — designed to elevate
          your business with clarity and confidence.
        </p>
        <div className="pricing-banner__taper" aria-hidden="true" />
      </header>

      <div className="pricing-toggle-wrap">
        <div className="pricing-toggle" role="group" aria-label="Billing period">
          <button
            type="button"
            className={cycle === "monthly" ? "is-active" : ""}
            onClick={() => setCycle("monthly")}
          >
            Monthly
          </button>
          <button
            type="button"
            className={cycle === "annual" ? "is-active" : ""}
            onClick={() => setCycle("annual")}
          >
            Annual <span className="pricing-toggle__save">save ~20%</span>
          </button>
        </div>
      </div>

      <div className="pricing-plans" aria-label="Membership plans">
        <section className="pricing-plan pricing-plan--agent">
          <div className="pricing-plan__top">
            <h2 className="pricing-plan__h1">{AGENT_PLAN.name}</h2>
            <p className="pricing-plan__h2">{AGENT_PLAN.subtitle}</p>
            <p className="pricing-plan__note">{AGENT_PLAN.note}</p>
          </div>
          <div className="pricing-plan__price-block">
            <p className="pricing-plan__price">
              <span className="pricing-plan__amount">{agentPrice.main}</span>
              {agentPrice.sub ? <span className="pricing-plan__period">{agentPrice.sub}</span> : null}
            </p>
          </div>
          <ul className="pricing-plan__features">
            {AGENT_PLAN.features.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="pricing-plan__footer">
            <button type="button" className="pricing-cta" onClick={() => openEnroll("agent")}>
              Choose Your Plan
            </button>
            <p className="pricing-plan__cta-note">{AGENT_PLAN.ctaNote}</p>
          </div>
        </section>

        <section className="pricing-plan pricing-plan--broker">
          <div className="pricing-plan__top">
            <h2 className="pricing-plan__h1">{BROKER_PLAN.name}</h2>
            <p className="pricing-plan__h2">{BROKER_PLAN.subtitle}</p>
            <p className="pricing-plan__note">{BROKER_PLAN.note}</p>
          </div>
          <div className="pricing-plan__price-block">
            <label className="pricing-plan__seats">
              <span>Office size</span>
              <select value={brokerSeat} onChange={(event) => setBrokerSeat(event.target.value)}>
                {SEAT_TIERS.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="pricing-plan__price">
              <span className="pricing-plan__amount">{brokerPrice.main}</span>
              {brokerPrice.sub ? (
                <span className="pricing-plan__period">{brokerPrice.sub}</span>
              ) : null}
            </p>
          </div>
          <ul className="pricing-plan__features">
            {BROKER_PLAN.features.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="pricing-plan__footer">
            <button type="button" className="pricing-cta" onClick={() => openEnroll("broker")}>
              Choose Your Plan
            </button>
            <p className="pricing-plan__cta-note">{BROKER_PLAN.ctaNote}</p>
          </div>
        </section>

        <section className="pricing-plan pricing-plan--multi">
          <div className="pricing-plan__top">
            <h2 className="pricing-plan__h1">{MULTI_OFFICE_PLAN.name}</h2>
            <p className="pricing-plan__h2">{MULTI_OFFICE_PLAN.subtitle}</p>
            <p className="pricing-plan__note">{MULTI_OFFICE_PLAN.note}</p>
          </div>
          <div className="pricing-plan__price-block">
            <p className="pricing-plan__price">
              <span className="pricing-plan__amount">Custom Pricing</span>
            </p>
          </div>
          <ul className="pricing-plan__features">
            {MULTI_OFFICE_PLAN.features.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="pricing-plan__footer">
            <Link className="pricing-cta" to="/contact">
              Contact Us
            </Link>
          </div>
        </section>

        <section className="pricing-plan pricing-plan--enterprise">
          <div className="pricing-plan__top">
            <h2 className="pricing-plan__h1">{ENTERPRISE_PLAN.name}</h2>
            <p className="pricing-plan__h2">{ENTERPRISE_PLAN.subtitle}</p>
            <p className="pricing-plan__note">{ENTERPRISE_PLAN.note}</p>
          </div>
          <div className="pricing-plan__price-block">
            <p className="pricing-plan__price">
              <span className="pricing-plan__amount">Custom Pricing</span>
            </p>
          </div>
          <ul className="pricing-plan__features">
            {ENTERPRISE_PLAN.features.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="pricing-plan__footer">
            <Link className="pricing-cta" to="/contact">
              Contact Us
            </Link>
          </div>
        </section>
      </div>

      <section className="pricing-trust" aria-labelledby="pricing-trust-title">
        <h2 id="pricing-trust-title">Trust &amp; confidence</h2>
        <ul className="pricing-trust__badges">
          {TRUST_BADGES.map((badge) => (
            <li key={badge}>{badge}</li>
          ))}
        </ul>
        <div className="pricing-trust__statements">
          <p>Your data belongs to you — always.</p>
          <p>Cancel anytime with no hidden fees.</p>
          <p>Professional-grade tools for modern real estate.</p>
          <p>Transparent billing. Transparent policies.</p>
        </div>
        <p className="pricing-agreement-link">
          <Link to="/purchase-agreement">Read the Purchase Agreement</Link>
          {" · "}
          <a href={`${APP_HOME_URL}/privacy`}>Privacy</a>
          {" · "}
          <a href={`${APP_HOME_URL}/terms`}>Terms</a>
        </p>
      </section>

      <section className="pricing-finale" aria-label="Membership enrollment">
        <div>
          <h2>Join the Professionals</h2>
          <p>Modern tools for modern real estate professionals.</p>
        </div>
        <button type="button" className="pricing-cta" onClick={() => openEnroll("agent")}>
          Get Started Today
        </button>
      </section>

      <MembershipEnrollmentModal
        isOpen={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        initialPlanId={enrollPlan}
        initialBrokerTier={brokerSeat}
        cycle={cycle}
      />
    </article>
  );
}
